/* eslint-disable @reatom/unit-naming-rule */
import { atom, filter, onConnect } from '@reatom/framework'
import { match } from 'ts-pattern'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth'
import { selectedNetworkAtom } from '@root/entities/network'
import { reatomPairInfoBulkSubscription } from '@root/entities/pair'
import { pairRegistry } from '@root/entities/pair/atoms'
import { deviceWalletIdsAtom } from '@root/features/secure-storage/atoms'
import { dataTableNoAccessError, reatomDataTable } from '@root/shared/ui-kit'

import * as FavoritePairChangeEvent from '../model/favorite-pair-change-event'
import { FavoriteUnavailable } from '../model/favorite-unavailable'
import { favoritePairsColumns } from '../ui/desktop/favorite-pairs-table/favorite-table-columns'

import { fetchFavoritePairsRequestAction } from './favorite-requests'
import { favoritePairsChangedEvent } from './reatom-favorite-pairs'

import type { UserFavouritePairsCreatePayload } from '@blade/trade-api-client'
import type { PairModel } from '@root/entities/pair'
import type { TableData } from '@root/shared/ui-kit'

export type FavoritePairsTableColumn =
  | 'currency'
  | 'mc'
  | 'liquidity'
  | 'holding'
  | 'priceChangePercent'
  | 'action'
  | 'pnl'

export const reatomFavoritePairsTable = () => {
  const pairInfoSubscriptionModel = reatomPairInfoBulkSubscription({
    name: 'favorite-pairs-table',
  })
  const paramsAtom = atom((ctx) => {
    const network = ctx.spy(selectedNetworkAtom)
    const walletIds = ctx.spy(deviceWalletIdsAtom)

    return { network, walletIds }
  }, 'paramsAtom').pipe(filter())

  const tableModel = reatomDataTable({
    columns: favoritePairsColumns,
    name: 'favoritePairs',
    paramsAtom,
    loadItems: async (
      ctx,
      {
        pagination: { limit, offset },
        params: { network, walletIds },
        sorting,
      },
    ) => {
      const isLoggedIn = ctx.get(isLoggedInAtom)
      if (!isLoggedIn) {
        return Result.fail(dataTableNoAccessError())
      }

      type SortByDto = UserFavouritePairsCreatePayload['sortBy']
      const sortBy = match(sorting?.sortColumn)
        .returnType<SortByDto>()
        .with('priceChangePercent', () => '5mGrowth')
        .with('holding', () => 'holding')
        .with('liquidity', () => 'liquidity')
        .with('mc', () => 'marketCap')
        .otherwise(() => undefined)

      const result = await fetchFavoritePairsRequestAction(ctx, {
        walletIds: [...walletIds],
        network,
        limit,
        offset,
        sortBy,
        sortOrder: sorting?.sortDirection,
      })

      return pipe(
        result,
        Result.map((data): TableData<PairModel | FavoriteUnavailable> => {
          return {
            data: data.data.map((dto) => {
              if (dto instanceof FavoriteUnavailable) {
                return dto
              }
              return pairRegistry.resolveFromDto(ctx, dto)
            }),
          }
        }),
      )
    },
    limit: 20,
    getId: (x) => x.address,
  })

  onConnect(tableModel, (ctx) => {
    const unsubscribeFavChangeEvent = favoritePairsChangedEvent.onCall(
      (ctx, payload) => {
        FavoritePairChangeEvent.match(payload, {
          FavoritePairAddedEvent: () => tableModel.load(ctx),
          FavoritePairRemovedEvent: ({ pairAddress }) => {
            tableModel.setState(
              ctx,
              AsyncStateN.map((state) => ({
                state,
                data: state.data.filter((it) => it.address !== pairAddress),
              })),
            )
          },
        })
      },
    )

    const unsubPairInfoSubscription = tableModel.onChange((ctx, pairsState) => {
      if (AsyncStateN.isError(pairsState)) {
        pairInfoSubscriptionModel.unsubscribeAll(ctx)
        return
      }
      if (AsyncStateN.isSuccess(pairsState)) {
        const pairs = pairsState.value.data.flatMap((it) =>
          it instanceof FavoriteUnavailable ? [] : [it],
        )

        pairInfoSubscriptionModel.processPairs(ctx, pairs)
        return
      }
    })

    return () => {
      unsubscribeFavChangeEvent()
      unsubPairInfoSubscription()
      pairInfoSubscriptionModel.unsubscribeAll(ctx)
    }
  })

  return tableModel
}

export type FavoritePairsTableModel = ReturnType<
  typeof reatomFavoritePairsTable
>
